import React from 'react'
import { motion, Variants } from 'framer-motion'

const capabilities = [
  {
    number: '01',
    title: 'AI Symptom Intelligence',
    description:
      'Smart analysis of user inputs to detect patterns & possibilities',
  },
  {
    number: '02',
    title: 'Differential Diagnosis Engine',
    description:
      'Generates multiple probable conditions with reasoning',
  },
  {
    number: '03',
    title: 'Remote Care Integration',
    description:
      'Seamless transition from insight to consultation',
  },
  {
    number: '04',
    title: 'Health Timeline Tracking',
    description:
      'Structured history for better long-term understanding',
  },
]

const leftVariant: Variants = {
  hidden: {
    opacity: 0,
    x: -120,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
}

const rightVariant: Variants = {
  hidden: {
    opacity: 0,
    x: 120,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
}

const numberLeftVariant: Variants = {
  hidden: {
    opacity: 0,
    x: -50,
    scale: 0.7,
  },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      duration: 0.9,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
}

const numberRightVariant: Variants = {
  hidden: {
    opacity: 0,
    x: 50,
    scale: 0.7,
  },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      duration: 0.9,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
}

const CoreCapabilities = () => {
  return (
    <section
      className="w-full py-7 md:py-15 xl:py-24"
      style={{
        background:
          'linear-gradient(293.91deg, #2B0946 1.94%, #5E0F9B 10%)',
      }}
    >
      <div className="mx-auto w-[90%] 2xl:mx-16">

        {/* Heading */}
        <motion.h2
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.3 }}
          transition={{
            duration: 0.8,
            ease: [0.25, 0.1, 0.25, 1],
          }}
          className="mb-8 text-center font-gilroy-extrabold text-2xl uppercase text-white md:mb-12 md:text-4xl xl:text-5xl"
        >
          Core Capabilities
        </motion.h2>

        {/* Cards */}
        <div className="flex flex-col gap-5 md:gap-10 lg:gap-14">

          {capabilities.map((item, index) => {
            const reverse = index % 2 !== 0

            return (
              <div
                key={item.number}
                className={`flex items-center gap-3 md:gap-6 lg:gap-10 ${
                  reverse
                    ? 'ml-0 flex-row md:ml-10 lg:ml-30'
                    : 'mr-0 flex-row md:mr-10 lg:mr-30'
                }`}
              >

                {/* Number Left */}
                {reverse && (
                  <motion.div
                    variants={numberLeftVariant}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.3 }}
                    className="flex w-[70px] shrink-0 justify-center md:w-[100px] lg:w-[140px]"
                  >
                    <span className="font-gilroy-bold italic skew-x-[-1deg] text-[35px] leading-none text-white md:text-[45px] lg:text-[62px]">
                      {item.number}
                    </span>
                  </motion.div>
                )}

                {/* Card */}
                <motion.div
                  variants={reverse ? rightVariant : leftVariant}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: false, amount: 0.3 }}
                  className={`flex-1 ${
                    reverse
                      ? 'mr-0 lg:mr-10'
                      : 'ml-0 lg:ml-10'
                  }`}
                >
                  <div className="border border-[#9e45da] bg-[#ac40f5e8]/50 px-5 py-4 backdrop-blur-sm md:px-6 lg:px-10 lg:py-6">

                    <h3 className="font-gilroy-bold text-lg text-white md:text-xl lg:text-2xl xl:text-3xl 2xl:text-4xl">
                      {item.title}
                    </h3>

                    <p className="mt-2 font-gilroy-medium text-sm leading-relaxed text-white/75 md:text-base lg:mt-3 lg:text-lg xl:text-lg 2xl:text-2xl">
                      {item.description}
                    </p>

                  </div>
                </motion.div>

                {/* Number Right */}
                {!reverse && (
                  <motion.div
                    variants={numberRightVariant}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.3 }}
                    className="flex w-[70px] shrink-0 justify-center md:w-[100px] lg:w-[140px]"
                  >
                    <span className="font-gilroy-bold italic skew-x-[-1deg] text-[35px] leading-none text-white md:text-[45px] lg:text-[62px]">
                      {item.number}
                    </span>
                  </motion.div>
                )}

              </div>
            )
          })}

        </div>
      </div>
    </section>
  )
}

export default CoreCapabilities